/**
 * Mini-tag inline para metadata estática. No interactivo (eso es <Chip>).
 * Siempre rounded-full. Texto corto (1-2 palabras).
 *
 * No usar para señales clínicas críticas — eso va con <DxClause tone="critical">
 * o <Banner tone="error">. Badge es decoración de metadata.
 *
 * @param {Object} props
 * @param {'default'|'info'|'success'|'warning'|'module'} [props.tone='default']
 * @param {'pae'|'prepara-internado'|'prepara-turno'|'revisa'|'bitacora'|'progreso'} [props.module]
 *   Solo aplica cuando tone='module'. Determina los tokens de color.
 * @param {'sm'|'md'} [props.size='sm']
 * @param {string} [props.className='']  Spacing externo (ml-2, etc.). No color.
 * @param {React.ReactNode} props.children
 */
export default function Badge({
  tone = 'default',
  module,
  size = 'sm',
  className = '',
  children,
}) {
  const toneClass =
    tone === 'module'
      ? MODULE_CLASSES[module] || MODULE_CLASSES.pae
      : TONE_CLASSES[tone] || TONE_CLASSES.default;

  const sizeClass = size === 'md' ? 'px-2.5 py-0.5 text-micro' : SIZE_SM;
  const borderClass = tone === 'module' ? MODULE_BORDER : '';

  return (
    <span
      className={[
        'inline-flex items-center rounded-full font-sans whitespace-nowrap',
        toneClass,
        sizeClass,
        borderClass,
        className,
      ].filter(Boolean).join(' ')}
    >
      {children}
    </span>
  );
}

// Tipografía sm: replica el patrón de los pills legacy del producto
// (10px uppercase tracking semibold). text-micro es 11px y queda muy grande
// para la mayoría de los badges sm.
const SIZE_SM =
  'text-10px uppercase tracking-wide font-semibold';

const TONE_CLASSES = {
  default: 'bg-surface-2 text-ink-muted',
  info:    'bg-accent-bg text-accent',
  success: 'bg-clinical-stable-bg text-clinical-stable',
  warning: 'bg-clinical-attention-bg text-clinical-attention',
};

// Static class names per module — Tailwind JIT no detecta template literals.
// DEUDA TÉCNICA — este mapping está duplicado en:
//   - foundational/ModuleHeader.jsx (MODULE_TOKENS)
//   - foundational/HomeCard.jsx (MODULE_ICON_CLASSES + MODULE_BG_VARS)
//   - foundational/Badge.jsx (MODULE_CLASSES)
//   - foundational/MenuItem.jsx (MODULE_ICON_CLASSES)
// Cuando agregués un módulo nuevo: actualizar las 4 entradas.
// Refactor a una fuente única de verdad pendiente (post-stores).
const MODULE_CLASSES = {
  pae:                 'bg-module-pae-tint text-module-pae',
  'pae-rapido':        'bg-module-pae-rapido-tint text-module-pae-rapido',
  'pae-detallado':     'bg-module-pae-detallado-tint text-module-pae-detallado',
  'prepara-practica':  'bg-module-prepara-practica-tint text-module-prepara-practica',
  'prepara-internado': 'bg-module-prepara-internado-tint text-module-prepara-internado',
  'prepara-turno':     'bg-module-prepara-turno-tint text-module-prepara-turno',
  revisa:              'bg-module-revisa-tint text-module-revisa',
  bitacora:            'bg-module-bitacora-tint text-module-bitacora',
  calendario:          'bg-module-calendario-tint text-module-calendario',
  progreso:            'bg-module-progreso-tint text-module-progreso',
  escalas:             'bg-module-escalas-tint text-module-escalas',
  medicamentos:        'bg-module-medicamentos-tint text-module-medicamentos',
  respira:             'bg-module-respira-tint text-module-respira',
  bienestar:           'bg-module-bienestar-tint text-module-bienestar',
  enciclopedia:        'bg-module-enciclopedia-tint text-module-enciclopedia',
  bibliografia:        'bg-module-bibliografia-tint text-module-bibliografia',
  ges:                 'bg-module-ges-tint text-module-ges',
};

// Border opcional para destacar Badge module sobre fondos similares
// (ej. badge module-pae sobre bg-paper). Per spec v2 §18: "border opcional
// para destacar". Mantenemos sutil sin definir nuevo token (decisión del
// doc 4g §20).
const MODULE_BORDER = 'border border-edge';
